import { useEffect, useRef, useState, useId, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import './MermaidBlock.css'
import { useMermaidZoomPan } from './useMermaidZoomPan'

/**
 * Interactive Mermaid diagram viewer with zoom, pan, and pinch-to-zoom.
 * Lazy-loads mermaid on first render. Provides a top-tier exploration UX
 * for large diagrams — auto-fits, smooth transitions, floating controls,
 * minimap, and full touch/trackpad support.
 */

interface MermaidApi {
    initialize: (config: Record<string, unknown>) => void
    render: (id: string, code: string, container?: HTMLElement) => Promise<{ svg: string }>
}

let mermaidPromise: Promise<MermaidApi> | null = null
function loadMermaid(): Promise<MermaidApi> {
    if (!mermaidPromise) {
        mermaidPromise = import('mermaid').then(m => {
            m.default.initialize({
                startOnLoad: false,
                theme: 'default',
                securityLevel: 'loose',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            })
            return m.default as unknown as MermaidApi
        }).catch(err => {
            mermaidPromise = null
            throw err
        })
    }
    return mermaidPromise
}

// ── Types ──────────────────────────────────────────────────────

interface MermaidBlockProps {
    children?: ReactNode
    securityLevel?: 'strict' | 'loose' | 'antiscript' | 'sandbox'
}

// ── Component ──────────────────────────────────────────────────

export default function MermaidBlock({ children, securityLevel = 'loose' }: MermaidBlockProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLDivElement>(null)
    const [svg, setSvg] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const uniqueId = useId().replace(/:/g, '_')
    const [showControls, setShowControls] = useState(true)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const controlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const code = typeof children === 'string'
        ? children
        : Array.isArray(children) ? children.join('') : String(children || '')

    // ── Controls auto-hide ───────────────────────────────────────

    const resetControlsTimer = () => {
        setShowControls(true)
        if (controlsTimer.current) clearTimeout(controlsTimer.current)
        controlsTimer.current = setTimeout(() => setShowControls(false), 3000)
    }

    // ── Mermaid rendering ────────────────────────────────────────
    // Mermaid.render() requires a globally unique ID per invocation.
    // useId() is stable across re-renders, so we append a monotonic
    // epoch counter to guarantee uniqueness when content changes
    // (e.g. from external WS updates while in preview mode).
    const renderEpoch = useRef(0)

    useEffect(() => {
        if (!code.trim()) return
        let active = true
        const epoch = ++renderEpoch.current

        const tempContainer = document.createElement('div')
        tempContainer.style.position = 'absolute'
        tempContainer.style.left = '-9999px'
        tempContainer.style.top = '-9999px'
        tempContainer.style.width = '0'
        tempContainer.style.height = '0'
        tempContainer.style.overflow = 'hidden'
        document.body.appendChild(tempContainer)

        const injectedNodes: Node[] = []
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node instanceof HTMLElement && node !== tempContainer) {
                        const isMermaidError =
                            (node.id && /^d\d+$/.test(node.id)) ||
                            node.classList.contains('error-icon') ||
                            node.querySelector?.('.error-icon') ||
                            node.textContent?.includes('Syntax error in text')
                        if (isMermaidError) {
                            injectedNodes.push(node)
                            node.remove()
                        }
                    }
                }
            }
        })
        observer.observe(document.body, { childList: true })

        loadMermaid().then(async (mermaid) => {
            if (!active) return
            try {
                mermaid.initialize({
                    startOnLoad: false,
                    theme: 'default',
                    securityLevel,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                })
                const { svg: rendered } = await mermaid.render(`mermaid${uniqueId}_${epoch}`, code.trim(), tempContainer)
                if (active) {
                    try { setSvg(rendered); setError(null) } catch { /* swallow React state tear */ }
                }
            } catch (err) {
                if (active) {
                    try { setError(err instanceof Error ? err.message : 'Mermaid rendering failed'); setSvg(null) } catch { /* swallow React state tear */ }
                }
            } finally {
                observer.disconnect()
                tempContainer.remove()
                document.querySelectorAll('[id^="d"][id$=""]').forEach(el => {
                    if (el instanceof HTMLElement && /^d\d+$/.test(el.id) &&
                        (el.querySelector('.error-icon') || el.textContent?.includes('Syntax error'))) {
                        el.remove()
                    }
                })
            }
        }).catch(err => {
            if (active) {
                try { setError(err instanceof Error ? err.message : 'Failed to load Mermaid'); setSvg(null) } catch { /* swallow React state tear */ }
            }
        })

        return () => {
            active = false
            observer.disconnect()
            tempContainer.remove()
        }
    }, [code, uniqueId, securityLevel])

    // ── Hook Integration ─────────────────────────────────────────

    const {
        transform,
        isDragging,
        animated,
        handlers,
        controls,
        fitScaleRef,
        intrinsicSize
    } = useMermaidZoomPan({
        containerRef: containerRef as RefObject<HTMLElement>,
        canvasRef: canvasRef as RefObject<HTMLElement>,
        svg,
        onInteraction: resetControlsTimer
    })

    // Intercept interactions to keep controls visible
    const wrappedHandlers = {
        ...handlers,
        onPointerDown: (e: React.PointerEvent) => {
            handlers.onPointerDown(e)
            resetControlsTimer()
        }
    }

    useEffect(() => {
        if (svg) resetControlsTimer()
        return () => { if (controlsTimer.current) clearTimeout(controlsTimer.current) }
    }, [svg])

    // ── Smart adaptive viewport height ────────────────────────────
    // Computes the ideal viewport height from the diagram's intrinsic
    // aspect ratio and the available container width. This gives each
    // diagram exactly the vertical space it needs — tall diagrams get
    // more height, wide ones stay compact. Re-computes on window resize
    // and when diagram content changes (external MCP updates).
    // Mobile-first: 50vh cap on small screens, 70vh on desktop.
    const [adaptiveHeight, setAdaptiveHeight] = useState<number | null>(null)
    const lastWidthRef = useRef(0) // track width to avoid height-triggered loops

    useEffect(() => {
        const container = containerRef.current
        if (!container || !intrinsicSize || !svg) {
            setAdaptiveHeight(null)
            return
        }

        const compute = () => {
            const containerWidth = container.clientWidth
            if (containerWidth <= 0) return

            // Only recompute if width actually changed (avoid height→resize→loop)
            if (Math.abs(containerWidth - lastWidthRef.current) < 2) return
            lastWidthRef.current = containerWidth

            const { width: svgW, height: svgH } = intrinsicSize
            const padding = 48 // breathing room around diagram

            // Scale diagram to fit container width, then derive ideal height
            const fitScale = Math.min(containerWidth / svgW, 1)
            const idealHeight = svgH * fitScale + padding

            // Mobile-first: smaller cap on small screens
            const isMobile = window.matchMedia('(max-width: 640px)').matches
            const maxH = window.innerHeight * (isMobile ? 0.5 : 0.7)
            const clamped = Math.max(200, Math.min(idealHeight, maxH))

            setAdaptiveHeight(prev => {
                // Dead-zone: skip update if height barely changed (< 2px)
                if (prev !== null && Math.abs(prev - clamped) < 2) return prev
                return clamped
            })
        }

        // Force first compute by resetting width tracker
        lastWidthRef.current = 0
        compute()

        // Re-compute on container width changes (window resize, layout shifts)
        const ro = new ResizeObserver(compute)
        ro.observe(container)
        return () => ro.disconnect()
    }, [intrinsicSize, svg])

    // Re-fit zoom/pan after adaptive height settles
    useEffect(() => {
        if (adaptiveHeight && svg) {
            // Wait for CSS transition to complete before soft-fitting again 
            // forceReset=false so we do not interrupt user interactions if height is resizing.
            const timer = setTimeout(() => controls.fitToView(false, false), 320)
            return () => clearTimeout(timer)
        }
    }, [adaptiveHeight])

    // ── Fullscreen modal ─────────────────────────────────────────

    const openFullscreen = () => setIsFullscreen(true)
    const closeFullscreen = () => setIsFullscreen(false)

    useEffect(() => {
        if (!isFullscreen) return
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                closeFullscreen()
            }
        }
        document.addEventListener('keydown', handler)
        return () => document.removeEventListener('keydown', handler)
    }, [isFullscreen])

    useEffect(() => {
        if (!isFullscreen) return
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = prev }
    }, [isFullscreen])

    // ── Minimap ──────────────────────────────────────────────────

    const zoomPercent = Math.round((transform.scale / fitScaleRef.current) * 100)
    const showMinimap = svg && zoomPercent > 120

    const minimapStyle = (() => {
        if (!showMinimap || !containerRef.current || !intrinsicSize || !svg) return undefined

        const containerRect = containerRef.current.getBoundingClientRect()
        const { width: svgW, height: svgH } = intrinsicSize

        // Minimap constants (match CSS)
        const mW = 100
        const mH = 60
        const r_m = mW / mH
        const r_s = svgW / svgH

        let cW, cH, cX, cY
        if (r_s > r_m) {
            cW = mW
            cH = mW / r_s
            cX = 0
            cY = (mH - cH) / 2
        } else {
            cH = mH
            cW = mH * r_s
            cX = (mW - cW) / 2
            cY = 0
        }

        // Viewport rect in diagram/SVG coordinates relative to viewBox origin
        // Cx = (Sx - minX) * scale + transform.x
        // 0 = (Sx_left - minX) * scale + transform.x  => Sx_left - minX = -x / scale
        const relVx = -transform.x / transform.scale
        const relVy = -transform.y / transform.scale
        const relVw = containerRect.width / transform.scale
        const relVh = containerRect.height / transform.scale

        return {
            content: {
                position: 'absolute' as const,
                left: `${cX}px`,
                top: `${cY}px`,
                width: `${cW}px`,
                height: `${cH}px`,
            },
            rect: {
                left: `${(relVx / svgW) * 100}%`,
                top: `${(relVy / svgH) * 100}%`,
                width: `${(relVw / svgW) * 100}%`,
                height: `${(relVh / svgH) * 100}%`,
            },
        }
    })()

    // ── Render ────────────────────────────────────────────────────

    if (error) {
        return (
            <div className="mermaid-error">
                <pre>{code}</pre>
                <span className="mermaid-error-msg">Diagram error: {error}</span>
            </div>
        )
    }

    if (!svg) {
        return (
            <div className="mermaid-loading">
                <div className="mermaid-spinner" />
                Renderar diagram…
            </div>
        )
    }

    return (
        <div
            className={`mermaid-viewport ${isDragging ? 'is-dragging' : ''}`}
            ref={containerRef}
            style={adaptiveHeight
                ? { height: `${adaptiveHeight}px`, touchAction: 'none', overscrollBehavior: 'none', userSelect: isDragging ? 'none' : 'auto', WebkitUserDrag: 'none' } as React.CSSProperties
                : { touchAction: 'none', overscrollBehavior: 'none', userSelect: isDragging ? 'none' : 'auto', WebkitUserDrag: 'none' } as React.CSSProperties}
            {...wrappedHandlers}
            onContextMenu={(e) => {
                if (isDragging || (e.nativeEvent as PointerEvent).pointerType === 'touch') {
                    e.preventDefault()
                }
            }}
        >
            <div
                className={`mermaid-canvas ${animated ? 'animated' : ''}`}
                ref={canvasRef}
                style={{
                    width: intrinsicSize ? `${intrinsicSize.width}px` : 'auto',
                    height: intrinsicSize ? `${intrinsicSize.height}px` : 'auto',
                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                }}
                dangerouslySetInnerHTML={{ __html: svg }}
            />

            {/* ── Zoom controls ─────────────────────────────────── */}
            <div
                className={`mermaid-controls ${showControls ? 'visible' : ''}`}
                data-no-drag
                onPointerEnter={() => setShowControls(true)}
                onPointerLeave={resetControlsTimer}
                onPointerDown={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    onClick={controls.zoomOut}
                    title="Zooma ut"
                    aria-label="Zoom out"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4 8h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                <span className="mermaid-zoom-badge" title="Zoom-nivå">
                    {zoomPercent}%
                </span>

                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    onClick={controls.zoomIn}
                    title="Zooma in"
                    aria-label="Zoom in"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 4v8M4 8h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                <div className="mermaid-ctrl-sep" />

                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    onClick={() => controls.fitToView(true)}
                    title="Anpassa till vy"
                    aria-label="Fit to view"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    onClick={controls.resetZoom}
                    title="100%"
                    aria-label="Reset to 100%"
                >
                    1:1
                </button>

                <div className="mermaid-ctrl-sep" />

                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    title="Zooma: Nyp eller Ctrl+Scroll&#10;Panorera: Dra eller Scroll&#10;Auto-anpassa: Dubbelklicka"
                    aria-label="Hjälp med kontroller"
                    style={{ cursor: 'help' }}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 11.5v.5M8 9.5a2 2 0 10-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                </button>

                <div className="mermaid-ctrl-sep" />

                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    onClick={openFullscreen}
                    title="Fullskärm"
                    aria-label="Open fullscreen"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 2h5M2 2v5M14 2h-5M14 2v5M2 14h5M2 14V9M14 14h-5M14 14V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

            {/* ── Minimap ───────────────────────────────────────── */}
            {showMinimap && minimapStyle && (
                <div className="mermaid-minimap">
                    <div className="mermaid-minimap-content" style={minimapStyle.content}>
                        <div
                            className="mermaid-minimap-inner"
                            dangerouslySetInnerHTML={{ __html: svg }}
                        />
                        <div
                            className="mermaid-minimap-viewport"
                            style={minimapStyle.rect}
                        />
                    </div>
                </div>
            )}

            {/* ── Fullscreen modal ────────────────────────────── */}
            {isFullscreen && createPortal(
                <MermaidFullscreenModal svg={svg} onClose={closeFullscreen} />,
                document.body
            )}
        </div>
    )
}

// ── Fullscreen Modal ─────────────────────────────────────────────

interface FullscreenModalProps {
    svg: string
    onClose: () => void
}

function MermaidFullscreenModal({ svg, onClose }: FullscreenModalProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLDivElement>(null)

    const {
        transform,
        isDragging,
        animated,
        handlers,
        controls,
        fitScaleRef,
        intrinsicSize
    } = useMermaidZoomPan({
        containerRef: containerRef as RefObject<HTMLElement>,
        canvasRef: canvasRef as RefObject<HTMLElement>,
        svg,
        onInteraction: () => { }
    })

    const zoomPercent = Math.round((transform.scale / fitScaleRef.current) * 100)

    return (
        <div className="mermaid-fs-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
            <div
                className={`mermaid-fs-container ${isDragging ? 'is-dragging' : ''}`}
                ref={containerRef}
                style={{
                    touchAction: 'none',
                    overscrollBehavior: 'none',
                    userSelect: isDragging ? 'none' : 'auto',
                    WebkitUserDrag: 'none'
                } as React.CSSProperties}
                {...handlers}
                onContextMenu={(e) => {
                    if (isDragging || (e.nativeEvent as PointerEvent).pointerType === 'touch') {
                        e.preventDefault()
                    }
                }}
            >
                <div
                    className={`mermaid-canvas ${animated ? 'animated' : ''}`}
                    ref={canvasRef}
                    style={{
                        width: intrinsicSize ? `${intrinsicSize.width}px` : 'auto',
                        height: intrinsicSize ? `${intrinsicSize.height}px` : 'auto',
                        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                    }}
                    dangerouslySetInnerHTML={{ __html: svg }}
                />
            </div>

            <div
                className="mermaid-fs-controls"
                data-no-drag
                onPointerDown={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    onClick={controls.zoomOut}
                    title="Zooma ut"
                    aria-label="Zoom out"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4 8h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                <span className="mermaid-zoom-badge">{zoomPercent}%</span>

                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    onClick={controls.zoomIn}
                    title="Zooma in"
                    aria-label="Zoom in"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 4v8M4 8h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                <div className="mermaid-ctrl-sep" />

                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    onClick={() => controls.fitToView(true)}
                    title="Anpassa"
                    aria-label="Fit to view"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                <div className="mermaid-ctrl-sep" />

                <button
                    type="button"
                    className="mermaid-ctrl-btn"
                    title="Zooma: Nyp eller Ctrl+Scroll&#10;Panorera: Dra eller Scroll&#10;Auto-anpassa: Dubbelklicka&#10;Stänga: Esc"
                    aria-label="Hjälp med kontroller"
                    style={{ cursor: 'help' }}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 11.5v.5M8 9.5a2 2 0 10-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                </button>

                <div className="mermaid-ctrl-sep" />

                <button
                    type="button"
                    className="mermaid-ctrl-btn mermaid-close-btn"
                    onClick={onClose}
                    title="Stäng"
                    aria-label="Close fullscreen"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>
            </div>
        </div>
    )
}
